import React, { Component } from 'react';
import { connect } from 'react-redux';
import { showNotification, translate } from 'react-admin';
import Card from '@material-ui/core/Card';
import CardContent from '@material-ui/core/CardContent';
import TextField from '@material-ui/core/TextField';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Switch from '@material-ui/core/Switch';
import Button from '@material-ui/core/Button';
import CachedIcon from '@material-ui/icons/Cached';
import SaveIcon from '@material-ui/icons/Save';
import api from '~config/api';

// The published layer is rebuilt in the background, so poll while it is working.
const POLL_MS = 5000;
const BUSY = ['PENDING', 'BUILDING', 'PUBLISHING'];

// Editable config fields, in form order. Everything else on the record is read-only
// status written by the rebuild task.
const FIELDS = [
  { name: 'publishedName', label: 'Nombre publicado (GeoServer)' },
  { name: 'title', label: 'Título (ES)' },
  { name: 'titleEn', label: 'Título (EN)' },
  { name: 'titlePt', label: 'Título (PT)' },
  { name: 'workspace', label: 'Workspace (opcional)' },
  { name: 'store', label: 'Store (opcional)' },
  { name: 'srs', label: 'SRS (opcional, ej. EPSG:4326)' },
];

const styles = {
  row: { display: 'block', marginBottom: 24, maxWidth: 720 },
  actions: { marginTop: 24, display: 'flex', gap: 12 },
  summary: {
    display: 'flex', gap: 24, flexWrap: 'wrap', padding: '12px 16px', marginBottom: 24,
    backgroundColor: '#f5f5f5', borderRadius: 4
  },
  skipped: {
    padding: '12px 16px', marginBottom: 24, backgroundColor: '#fff3e0',
    border: '1px solid #ffb74d', borderRadius: 4
  },
  error: {
    padding: '12px 16px', marginBottom: 24, backgroundColor: '#ffebee',
    border: '1px solid #e57373', borderRadius: 4, color: '#b71c1c'
  }
};

function Summary({ record }) {
  return (
    <div style={styles.summary}>
      <span><b>Estado:</b> {record.status}</span>
      <span><b>Proyectos:</b> {record.projectCount}</span>
      <span><b>Features:</b> {record.featureCount}</span>
      <span><b>Tabla:</b> {record.publishedTable || '-'}</span>
      <span><b>Última reconstrucción:</b> {record.lastRebuildAt || '-'}</span>
    </div>
  );
}

class ProjectsLayerScreen extends Component {
  state = { record: null, saving: false };

  componentDidMount() {
    this._mounted = true;
    this.load();
    this.interval = setInterval(() => {
      // Only poll while a rebuild is in flight; otherwise this screen is a plain form
      // and refetching would fight with whatever the operator is typing.
      if (this.state.record && BUSY.includes(this.state.record.status)) {
        this.load();
      }
    }, POLL_MS);
  }

  componentWillUnmount() {
    this._mounted = false;
    clearInterval(this.interval);
  }

  load = () =>
    api.get('/projects-layer/').then(response => {
      if (!this._mounted || !response.ok) {
        return;
      }
      const data = response.data;
      const record = Array.isArray(data) ? data[0] : (data.page && data.page[0]) || data;
      this.setState({ record });
    });

  change = (name, value) =>
    this.setState(state => ({ record: { ...state.record, [name]: value } }));

  // The .sld upload is read here rather than posted as multipart: api's request
  // transform runs cerealizr over request.data, which turns a FormData into `{}` and
  // silently saves nothing. Reading the file to text keeps the whole payload plain JSON,
  // and the same transform then camelCase->snake_cases the keys for the API.
  readSldFile = event => {
    const file = event.target.files[0];
    if (!file) {
      return;
    }
    const reader = new FileReader();
    reader.onload = () => this.change('sld', reader.result);
    reader.readAsText(file);
  };

  save = () => {
    const { record } = this.state;
    const { dispatch } = this.props;
    const body = { enabled: !!record.enabled, sld: record.sld || '' };
    FIELDS.forEach(f => {
      body[f.name] = record[f.name] || '';
    });
    this.setState({ saving: true });
    api.put('/projects-layer/1/', body).then(response => {
      this.setState({ saving: false });
      if (response.ok) {
        dispatch(showNotification('projectsLayer.saved'));
        this.load();
      } else {
        dispatch(showNotification(detail(response), 'warning'));
      }
    });
  };

  rebuild = () => {
    const { dispatch } = this.props;
    api.post('/projects-layer/rebuild/').then(response => {
      if (response.ok) {
        dispatch(showNotification('projectsLayer.rebuildStarted'));
        this.load();
      } else {
        dispatch(showNotification('projectsLayer.rebuildError', 'warning'));
      }
    });
  };

  render() {
    const { record, saving } = this.state;
    if (!record) {
      return null;
    }
    return (
      <Card>
        <CardContent>
          <h2>Capa de Proyectos</h2>
          <p>
            Capa combinada con la geometría de todos los proyectos publicados. Se
            reconstruye automáticamente cuando se publica, despublica o edita un proyecto.
          </p>

          <Summary record={record} />

          {record.errorMessage ? (
            <div style={styles.error}><b>Error:</b> {record.errorMessage}</div>
          ) : null}

          {record.skippedCount > 0 ? (
            <div style={styles.skipped}>
              <b>{record.skippedCount} proyecto(s) omitido(s)</b> por no poder leerse:{' '}
              {(record.skipped || []).map(s => s.codigo).join(', ')}
            </div>
          ) : null}

          <div style={styles.row}>
            <FormControlLabel
              control={
                <Switch
                  checked={!!record.enabled}
                  onChange={e => this.change('enabled', e.target.checked)}
                />
              }
              label="Publicar en GeoServer"
            />
          </div>

          {FIELDS.map(f => (
            <div key={f.name} style={styles.row}>
              <TextField
                label={f.label}
                value={record[f.name] || ''}
                onChange={e => this.change(f.name, e.target.value)}
                fullWidth
              />
            </div>
          ))}

          <div style={styles.row}>
            <TextField
              label="Estilo (SLD)"
              value={record.sld || ''}
              onChange={e => this.change('sld', e.target.value)}
              multiline
              rows={6}
              fullWidth
            />
          </div>

          <div style={styles.row}>
            <input type="file" accept=".sld" onChange={this.readSldFile} />
          </div>

          <div style={styles.actions}>
            <Button variant="contained" color="primary" onClick={this.save} disabled={saving}>
              <SaveIcon style={{ marginRight: 8 }} fontSize="small" />
              Guardar
            </Button>
            <Button color="primary" onClick={this.rebuild}>
              <CachedIcon style={{ marginRight: 8 }} fontSize="small" />
              Reconstruir ahora
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }
}

function detail(response) {
  const data = response.data || {};
  const first = Object.values(data)[0];
  return Array.isArray(first) ? first[0] : data.detail || 'No se pudo guardar';
}

export default connect()(translate(ProjectsLayerScreen));
